import { useState } from 'react';
import './Inventory.css';

const ITEM_TYPE_ICONS = {
  weapon: '⚔️',
  armor: '🛡️',
  consumable: '🧪',
  food: '🍖',
  drink: '🍺',
  tool: '🔧',
  treasure: '💎',
  misc: '📦'
};

const ITEM_TYPE_LABELS = {
  weapon: 'Weapon',
  armor: 'Armor',
  consumable: 'Consumable',
  food: 'Food',
  drink: 'Drink',
  tool: 'Tool',
  treasure: 'Treasure',
  misc: 'Miscellaneous'
};

// Items that can be used/consumed
const USABLE_TYPES = ['consumable', 'food', 'drink'];

export default function Inventory({ character, onClose, onDropItem, onUseItem }) {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [confirmDrop, setConfirmDrop] = useState(null);

  const inventory = character?.inventory || [];
  const strength = character?.abilityScores?.strength || 10;

  // Carrying capacity: Strength score x 15 lbs
  const maxCarryWeight = strength * 15;

  // Calculate total weight
  const totalWeight = inventory.reduce((sum, item) => {
    return sum + (item.weight || 0) * (item.quantity || 1);
  }, 0);

  // Encumbrance thresholds (variant rule)
  const encumberedThreshold = strength * 5;
  const heavilyEncumberedThreshold = strength * 10;

  const getEncumbranceStatus = () => {
    if (totalWeight >= maxCarryWeight) return { status: 'overloaded', color: 'var(--health-low)', label: 'Overloaded!' };
    if (totalWeight >= heavilyEncumberedThreshold) return { status: 'heavily', color: 'var(--health-mid)', label: 'Heavily Encumbered' };
    if (totalWeight >= encumberedThreshold) return { status: 'encumbered', color: 'var(--accent-gold)', label: 'Encumbered' };
    return { status: 'normal', color: 'var(--health-high)', label: 'Normal' };
  };

  const encumbrance = getEncumbranceStatus();
  const weightPercentage = Math.min((totalWeight / maxCarryWeight) * 100, 100);

  // Filter items by category
  const filteredItems = selectedCategory === 'all'
    ? inventory
    : inventory.filter(item => item.type === selectedCategory);

  // Get unique categories from inventory
  const categories = ['all', ...new Set(inventory.map(item => item.type))];

  const formatValue = (value) => {
    if (!value) return 'No value';
    const parts = [];
    if (value.gold > 0) parts.push(`${value.gold} gp`);
    if (value.silver > 0) parts.push(`${value.silver} sp`);
    if (value.copper > 0) parts.push(`${value.copper} cp`);
    return parts.length > 0 ? parts.join(', ') : 'No value';
  };

  const handleDropItem = (item) => {
    if (confirmDrop === item._id) {
      onDropItem?.(item._id);
      setConfirmDrop(null);
    } else {
      setConfirmDrop(item._id);
      // Reset confirm after 3 seconds
      setTimeout(() => setConfirmDrop(null), 3000);
    }
  };

  // Helper function to check for whole word matches (not substrings)
  // This prevents "stale" from matching "ale", etc.
  const matchesKeyword = (name, keywords) => {
    const words = name.split(/[\s\-_,]+/);
    return keywords.some(keyword => words.includes(keyword));
  };

  const isItemUsable = (item) => {
    // Check if item is empty (uses = 0)
    if (item.properties?.uses === 0) {
      return false;
    }

    // Check if item is usable by type
    if (USABLE_TYPES.includes(item.type)) {
      return true;
    }

    // Check by name for common food/drink items
    const nameLower = item.name.toLowerCase();
    const foodKeywords = ['bread', 'food', 'ration', 'meat', 'fish', 'cheese', 'fruit', 'apple', 'berry', 'stew', 'soup', 'pie', 'cake', 'meal', 'jerky', 'loaf', 'roll', 'biscuit'];
    const drinkKeywords = ['water', 'waterskin', 'ale', 'wine', 'mead', 'juice', 'milk', 'tea', 'drink', 'flask', 'bottle', 'potion', 'beer', 'cider', 'rum', 'whiskey', 'brandy'];

    return matchesKeyword(nameLower, foodKeywords) || matchesKeyword(nameLower, drinkKeywords);
  };

  const handleUseItem = (item) => {
    if (isItemUsable(item)) {
      onUseItem?.(item.name);
    }
  };

  return (
    <div className="inventory-overlay">
      <div className="inventory-panel">
        {/* Header */}
        <div className="inventory-header">
          <h2>Inventory</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Weight/Encumbrance Bar */}
        <div className="encumbrance-section">
          <div className="encumbrance-header">
            <span className="encumbrance-label">Carrying Capacity</span>
            <span className="encumbrance-status" style={{ color: encumbrance.color }}>
              {encumbrance.label}
            </span>
          </div>
          <div className="encumbrance-bar">
            <div
              className="encumbrance-fill"
              style={{
                width: `${weightPercentage}%`,
                backgroundColor: encumbrance.color
              }}
            />
            <div
              className="encumbrance-marker encumbered-marker"
              style={{ left: `${(encumberedThreshold / maxCarryWeight) * 100}%` }}
              title="Encumbered threshold"
            />
            <div
              className="encumbrance-marker heavily-marker"
              style={{ left: `${(heavilyEncumberedThreshold / maxCarryWeight) * 100}%` }}
              title="Heavily encumbered threshold"
            />
          </div>
          <div className="encumbrance-values">
            <span>{totalWeight.toFixed(1)} lbs</span>
            <span>{maxCarryWeight} lbs max</span>
          </div>
        </div>

        {/* Currency Display */}
        <div className="currency-section">
          <span className="currency-item gold">
            <span className="currency-icon">●</span>
            {character?.currency?.gold || 0} gp
          </span>
          <span className="currency-item silver">
            <span className="currency-icon">●</span>
            {character?.currency?.silver || 0} sp
          </span>
          <span className="currency-item copper">
            <span className="currency-icon">●</span>
            {character?.currency?.copper || 0} cp
          </span>
        </div>

        {/* Category Filters */}
        <div className="inventory-filters">
          {categories.map(cat => (
            <button
              key={cat}
              className={`filter-btn ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat === 'all' ? 'All' : (
                <>
                  <span className="filter-icon">{ITEM_TYPE_ICONS[cat]}</span>
                  {ITEM_TYPE_LABELS[cat]}
                </>
              )}
            </button>
          ))}
        </div>

        {/* Items Grid */}
        <div className="inventory-items">
          {filteredItems.length === 0 ? (
            <div className="inventory-empty">
              <p>No items{selectedCategory !== 'all' ? ` of type "${ITEM_TYPE_LABELS[selectedCategory]}"` : ''}</p>
            </div>
          ) : (
            filteredItems.map((item) => (
              <div
                key={item._id}
                className={`inventory-item ${item.equipped ? 'equipped' : ''} ${hoveredItem === item._id ? 'hovered' : ''}`}
                onMouseEnter={() => setHoveredItem(item._id)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <div className="item-main">
                  <span className="item-icon">{ITEM_TYPE_ICONS[item.type] || '📦'}</span>
                  <div className="item-info">
                    <span className="item-name">
                      {item.name}
                      {item.quantity > 1 && <span className="item-quantity"> x{item.quantity}</span>}
                    </span>
                    <span className="item-type">{ITEM_TYPE_LABELS[item.type] || 'Item'}</span>
                  </div>
                  {item.equipped && <span className="equipped-badge">Equipped</span>}
                </div>

                {/* Expanded details on hover */}
                {hoveredItem === item._id && (
                  <div className="item-details">
                    {item.description && (
                      <p className="item-description">{item.description}</p>
                    )}
                    <div className="item-stats">
                      <span className="item-stat">
                        <span className="stat-icon">⚖️</span>
                        {item.weight ? `${item.weight} lbs` : 'Weightless'}
                        {item.quantity > 1 && ` (${(item.weight * item.quantity).toFixed(1)} total)`}
                      </span>
                      <span className="item-stat">
                        <span className="stat-icon">💰</span>
                        {formatValue(item.value)}
                      </span>
                    </div>

                    {/* Item Properties */}
                    {item.properties && Object.keys(item.properties).length > 0 && (
                      <div className="item-properties">
                        {item.properties.damage && (
                          <span className="property">Damage: {item.properties.damage}</span>
                        )}
                        {item.properties.armorClass && (
                          <span className="property">AC: {item.properties.armorClass}</span>
                        )}
                        {item.properties.durability !== undefined && (
                          <span className="property">Durability: {item.properties.durability}%</span>
                        )}
                        {item.properties.charges !== undefined && (
                          <span className="property">Charges: {item.properties.charges}</span>
                        )}
                        {item.properties.uses !== undefined && (
                          <span className="property">
                            Uses: {item.properties.uses > 0 ? item.properties.uses : 'Empty'}
                          </span>
                        )}
                        {item.properties.range && (
                          <span className="property">Range: {item.properties.range}</span>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="item-actions">
                      {isItemUsable(item) && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleUseItem(item)}
                        >
                          Use
                        </button>
                      )}
                      <button
                        className={`btn btn-sm ${confirmDrop === item._id ? 'btn-danger' : 'btn-secondary'}`}
                        onClick={() => handleDropItem(item)}
                      >
                        {confirmDrop === item._id ? 'Confirm Drop' : 'Drop'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="inventory-footer">
          <span className="item-count">{inventory.length} item{inventory.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
}
